import React, { useEffect, useState } from 'react';
import {
  Prohibit, ClockCounterClockwise, Info, ArrowClockwise, Check, X,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import {
  getBranchNames, getCommencedGroupCodes, getTickets, cancelChitGroup, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';

const CHANGE_TYPE = 'CHIT_GROUP_CANCEL';

// The group can be cancelled only once its status is Completed.
const CANCELLABLE_STATUS = 'completed';

const isCancellable = (status) => String(status || '').trim().toLowerCase() === CANCELLABLE_STATUS;

function ChitGroupCancelForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');
  const [chitGroupStatus, setChitGroupStatus] = useState('');
  const [reason, setReason] = useState('');

  const [branchschema, setBranchschema] = useState(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const noDataFound = (what) => showToast('warn', 'No Data Found', `No ${what} found for the current selection.`);

  useEffect(() => {
    getBranchNames()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setBranchlist(list);
        if (list.length === 0) noDataFound('branches');
      })
      .catch(() => showToast('error', 'Load Failed', 'Failed to load branches. Please try again.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setChitGroupStatus('');
  };

  const resetFromGroup = () => {
    setTicket('');
    setTickets([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setGroup('');
    setGroupList([]);
    resetFromGroup();
  };

  const handleCaoChange = async (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;

    const selected = branchlist.find((x) => x.branch_name === value);
    if (!selected) return;

    setBranchschema(selected.branch_code);

    try {
      const res = await getCommencedGroupCodes(selected.branch_code, selected.branch_code);
      const list = Array.isArray(res) ? res : [];
      setGroupList(list);
      if (list.length === 0) noDataFound('group codes');
    } catch (err) {
      console.error('[getCommencedGroupCodes] error =', err);
      setGroupList([]);
      showToast('error', 'Load Failed', 'Failed to load group codes. Please try again.');
    }
  };

  const handleGroupChange = async (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;

    try {
      const res = await getTickets(branchschema, value);
      const list = Array.isArray(res) ? res : [];
      setTickets(list);
      if (list.length === 0) noDataFound('tickets');
    } catch (err) {
      console.error('[getTickets] error =', err);
      setTickets([]);
      showToast('error', 'Load Failed', 'Failed to load tickets. Please try again.');
    }
  };

  // The status comes from the same GetcommencedGroupcodes list the group dropdown uses.
  const handleTicketChange = (value) => {
    const next = value === '' || value === null || value === undefined ? '' : value;
    setTicket(next);
    resetFromTicket();
    if (next === '') return;

    const selected = groupList.find((x) => x.groupcode === group);
    setChitGroupStatus(selected?.chitgroup_status ?? '');
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && ticket === '';
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const canCancel = isCancellable(chitGroupStatus);

  const handleSave = () => {
    setTouched(true);

    if (!cao || !group || ticket === '') {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!canCancel) {
      showToast('warn', 'Cannot Cancel', 'Only a chit group whose status is Completed can be cancelled.');
      return;
    }

    if (reason.trim().length < 10 || reason.length > 200) {
      showToast('warn', 'Incomplete Form', 'Please enter a reason between 10 and 200 characters.');
      return;
    }

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const confirmSave = async () => {
    setSaving(true);

    const selectedGroup = groupList.find((x) => x.groupcode === group);

    const payload = {
      branchSchema: branchschema,
      chitgroupId: selectedGroup?.chitgroup_id,
      ticketNo: Number(ticket),
      chitAdvanceStatus: chitGroupStatus,
    };

    try {
      // The endpoint only updates when the status is Completed.
      if (!canCancel || !payload.chitgroupId) {
        throw new Error('Only a Completed chit group can be cancelled.');
      }

      const res = await cancelChitGroup(payload);
      const message = typeof res === 'string' ? res : res?.message;
      if (res === false || (message && !/success/i.test(message))) {
        setSaving(false);
        setShowConfirmModal(false);
        showToast('error', 'Update Failed', message || 'The chit group could not be cancelled.', true);
        return;
      }

      const audit = {
        schemaName: branchschema,
        loginName,
        loginTime: new Date().toISOString(),
        changeType: CHANGE_TYPE,
        oldData: { groupCode: group, ticketNo: ticket, chitGroupStatus },
        newData: { groupCode: group, ticketNo: ticket, chitGroupStatus: 'Cancelled' },
        reason: reason.trim(),
      };

      try {
        await saveData([audit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Chit group cancelled and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Chit group cancelled, but audit log failed to save.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not cancel the chit group. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setShowConfirmModal(false);
  };

  const ro = { fontSize: '13px', background: '#F9FAFB' };
  const smLabel = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' };
  const req = <span style={{ color: '#DC2626' }}>*</span>;

  const statusHint = chitGroupStatus === ''
    ? 'Shown once a ticket is selected'
    : canCancel
      ? 'Completed — this group can be cancelled'
      : 'Only a Completed chit group can be cancelled';
  const statusHintColor = chitGroupStatus === '' ? '#9CA3AF' : canCancel ? '#059669' : '#D97706';

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="chit-group-cancel-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="chit-group-cancel-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Prohibit size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Group</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Chit Advance Cancel</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Chit Group */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Chit Group</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to see the chit group status</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={smLabel}>Branch Name {req}</label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="chit-group-cancel-branch"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={smLabel}>Group Code {req}</label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groupList}
              getLabel={(o) => o.groupcode}
              getValue={(o) => o.groupcode}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="chit-group-cancel-group"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={smLabel}>Ticket No {req}</label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!group}
              error={ticketInvalid}
              testId="chit-group-cancel-ticket"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={smLabel}>Chit Group Status</label>
            <input className="input-field" style={ro} value={chitGroupStatus} readOnly placeholder="—" data-testid="chit-group-cancel-status" />
            <div style={{ fontSize: '11px', color: statusHintColor, marginTop: '2px' }} data-testid="chit-group-cancel-status-hint">
              {statusHint}
            </div>
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={ticket === ''} hint="Select a branch, group code and ticket in section A first">
          {/* SECTION B: Reason */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason {req}</label>
            <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
              {reason.length} / 200
            </span>
          </div>
          <textarea
            className="input-field"
            style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
            rows={3}
            maxLength={200}
            placeholder="Enter reason (min 10 characters)..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="chit-group-cancel-reason"
          />
          {reasonInvalid && (
            <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
              {reason.trim().length === 0 ? 'Reason is required' : 'Minimum 10 characters required'}
            </div>
          )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="chit-group-cancel-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving || !canCancel} data-testid="chit-group-cancel-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '460px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="chit-group-cancel-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Prohibit size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Chit Advance Cancel</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to cancel chit group
                {' '}
                <strong>{group}</strong>
                {' '}
                (ticket
                {' '}
                <strong>{ticket}</strong>
                ). Please review before confirming.
              </p>

              <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px' }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Branch</p>
                  <p style={{ fontSize: '13px', color: '#111827', fontWeight: '600', margin: 0 }}>{cao}</p>
                </div>
                <div style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: '8px', padding: '10px' }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Chit Group Status</p>
                  <p style={{ fontSize: '13px', color: '#111827', fontWeight: '600', margin: 0 }}>{chitGroupStatus}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{reason.trim()}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="chit-group-cancel-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="chit-group-cancel-confirm-update">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Cancel Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChitGroupCancelForm;
